import { sanitizeRevitParameterNames } from '../../../../shared/lib/revitParameterNames';
import { collectAdditionalCategoryTargets } from '../../../../shared/lib/hooks/useParameterAddition';
import {
  collectBoltConfiguredParameters,
  getFormFieldValue,
  type MappingProjectParameterReference,
  type MappingProjectReadinessInput,
} from './mappedParameterReferences';
import type { MappingProjectParameterNameGroups } from './readinessDiff';

export type MappingProjectReadinessStatus =
  | 'checking'
  | 'repairing'
  | 'ready'
  | 'blocked'
  | 'unavailable';

export interface MappingProjectReadinessResult {
  status: MappingProjectReadinessStatus;
  isBlocking: boolean;
  blockReason: 'profileConfirmation' | 'projectInspection' | 'missingParameters' | 'familyRepairRequired' | null;
  missingParameterNames: string[];
  blockedParameterNames: string[];
  repairableParameterNames: string[];
  message: string | null;
  retry: () => void;
}

function formatMissingMessage(names: string[]): string {
  const visible = names.slice(0, 6).join(', ');
  const suffix = names.length > 6 ? `, +${names.length - 6} more` : '';
  return `${names.length} mapped Revit parameter${names.length === 1 ? '' : 's'} missing in this project: ${visible}${suffix}`;
}

function formatRepairMessage(names: string[]): string {
  if (names.length === 0) {
    return 'Creating missing PipeEX setup parameters for this mapping profile.';
  }

  const visible = names.slice(0, 6).join(', ');
  const suffix = names.length > 6 ? `, +${names.length - 6} more` : '';
  return `Creating ${names.length} missing PipeEX setup parameter${names.length === 1 ? '' : 's'}: ${visible}${suffix}`;
}

function formatList(labels: string[]): string {
  return labels.join(', ');
}

export function buildMappingFamilyRepairBlockedMessage(input: {
  familyGroups: string[];
  quickFillAttempted: boolean;
  quickFillSavedCount?: number;
  quickFillError?: string | null;
  missingParameterNames?: string[];
}): string | null {
  const familyGroups = Array.from(
    new Set(input.familyGroups.map((group) => group.trim()).filter(Boolean))
  ).sort((left, right) => left.localeCompare(right));

  if (familyGroups.length === 0) {
    return null;
  }

  const quickFillPrefix = input.quickFillError
    ? `PipeEX created the available setup parameters, but Quick Fill auto-fill could not save every generated classification: ${input.quickFillError}.`
    : input.quickFillAttempted
      ? `PipeEX created the available setup parameters and ran Quick Fill auto-fill to classify this project${(input.quickFillSavedCount ?? 0) > 0 ? `, saving ${input.quickFillSavedCount} generated value${input.quickFillSavedCount === 1 ? '' : 's'} to Revit` : ''}.`
      : 'PipeEX created the available setup parameters.';

  const missingParameterNames = input.missingParameterNames ?? [];
  const missingParametersSuffix = missingParameterNames.length > 0
    ? ` Not found in this project: ${formatList(missingParameterNames.slice(0, 6))}${(missingParameterNames.length > 6 ? `, +${missingParameterNames.length - 6} more` : '')}.`
    : '';

  return `${quickFillPrefix} Full setup is still blocked because no editable matching families were found for: ${formatList(familyGroups)}. These families are required before PipeEX can add the bolt and additional-category parameters.${missingParametersSuffix} Add or classify matching families, then select Retry setup.`;
}

export function isFamilyRepairBlockedMessage(message: string | null | undefined): boolean {
  return typeof message === 'string' && message.includes('no editable matching families were found');
}

function createReadinessResult(input: {
  status: MappingProjectReadinessStatus;
  isBlocking: boolean;
  blockReason?: MappingProjectReadinessResult['blockReason'];
  message: string | null;
  retry: () => void;
  names?: MappingProjectParameterNameGroups;
}): MappingProjectReadinessResult {
  return {
    status: input.status,
    isBlocking: input.isBlocking,
    blockReason: input.blockReason ?? null,
    missingParameterNames: input.names?.missing ?? [],
    blockedParameterNames: input.names?.blocked ?? [],
    repairableParameterNames: input.names?.repairable ?? [],
    message: input.message,
    retry: input.retry,
  };
}

export function resolveMappingProjectReadinessResult(input: {
  enabled: boolean;
  hasReferences: boolean;
  hostRuntimeAvailable: boolean;
  projectKey: string | null;
  catalogReady: boolean;
  catalogLoading: boolean;
  catalogUnavailable: boolean;
  parametersError: string | null;
  names: MappingProjectParameterNameGroups;
  autoRepair: boolean;
  autoRepairBlockedMessage?: string | null;
  familyRepairBlockedMessage?: string | null;
  scopeConflictMessage?: string | null;
  hasAttemptedRepair: boolean;
  isRepairing: boolean;
  repairErrorMessage: string | null;
  retry: () => void;
}): MappingProjectReadinessResult {
  if (!input.enabled || !input.hasReferences) {
    return createReadinessResult({
      status: 'ready',
      isBlocking: false,
      message: null,
      retry: input.retry,
    });
  }

  if (!input.hostRuntimeAvailable) {
    return createReadinessResult({
      status: 'unavailable',
      isBlocking: false,
      message: null,
      retry: input.retry,
    });
  }

  if (!input.projectKey) {
    return createReadinessResult({
      status: 'checking',
      isBlocking: true,
      blockReason: 'projectInspection',
      message: 'Waiting for the active Revit project before saving this mapping profile.',
      retry: input.retry,
    });
  }

  // A scoped repair failure outranks a clean-looking diff: Quick Fill value
  // failures and verify-reported misses are invisible to the name-based diff,
  // so 'ready' would otherwise mask a repair the user must retry.
  if (input.repairErrorMessage) {
    return createReadinessResult({
      status: 'blocked',
      isBlocking: true,
      blockReason: isFamilyRepairBlockedMessage(input.repairErrorMessage)
        ? 'familyRepairRequired'
        : 'missingParameters',
      message: input.repairErrorMessage,
      names: input.names,
      retry: input.retry,
    });
  }

  if (input.catalogReady && input.names.missing.length === 0) {
    return createReadinessResult({
      status: 'ready',
      isBlocking: false,
      message: null,
      retry: input.retry,
    });
  }

  if (input.isRepairing) {
    return createReadinessResult({
      status: 'repairing',
      isBlocking: true,
      blockReason: 'projectInspection',
      message: formatRepairMessage(input.names.repairable),
      names: input.names,
      retry: input.retry,
    });
  }

  if (input.catalogUnavailable) {
    return createReadinessResult({
      status: 'blocked',
      isBlocking: true,
      blockReason: 'projectInspection',
      message: input.parametersError || 'Could not inspect active project parameters.',
      retry: input.retry,
    });
  }

  if (!input.catalogReady || input.catalogLoading) {
    return createReadinessResult({
      status: 'checking',
      isBlocking: true,
      blockReason: 'projectInspection',
      message: 'Checking project parameters in the active Revit project.',
      retry: input.retry,
    });
  }

  if (input.scopeConflictMessage) {
    return createReadinessResult({
      status: 'blocked',
      isBlocking: true,
      blockReason: 'missingParameters',
      message: input.scopeConflictMessage,
      names: input.names,
      retry: input.retry,
    });
  }

  if (
    input.autoRepair &&
    input.names.repairable.length > 0 &&
    !input.hasAttemptedRepair
  ) {
    return createReadinessResult({
      status: 'repairing',
      isBlocking: true,
      message: formatRepairMessage(input.names.repairable),
      names: input.names,
      retry: input.retry,
    });
  }

  if (!input.autoRepair && input.autoRepairBlockedMessage && input.names.repairable.length > 0) {
    return createReadinessResult({
      status: 'blocked',
      isBlocking: true,
      blockReason: 'profileConfirmation',
      message: input.autoRepairBlockedMessage,
      names: input.names,
      retry: input.retry,
    });
  }

  return createReadinessResult({
    status: 'blocked',
    isBlocking: true,
    blockReason: input.familyRepairBlockedMessage ||
      isFamilyRepairBlockedMessage(input.repairErrorMessage)
      ? 'familyRepairRequired'
      : 'missingParameters',
    message: input.repairErrorMessage ??
      input.familyRepairBlockedMessage ??
      formatMissingMessage(input.names.missing),
    names: input.names,
    retry: input.retry,
  });
}

export function buildMappingReadinessSignature(input: {
  projectKey: string | null;
  profileKey: string;
  references: MappingProjectParameterReference[];
  setupSignature?: unknown;
}): string {
  return JSON.stringify({
    projectKey: input.projectKey ?? 'no-project',
    profileKey: input.profileKey || 'unscoped',
    references: input.references.map((reference) => [
      reference.name,
      [...reference.sources].sort(),
      [...reference.repairKinds].sort(),
    ]),
    setup: input.setupSignature ?? null,
  });
}

export function buildMappingReadinessSetupSignature(input: MappingProjectReadinessInput): unknown {
  const sortedCategories = (categories: unknown): string[] =>
    Array.isArray(categories)
      ? Array.from(
          new Set(categories.filter((category): category is string => typeof category === 'string'))
        ).sort((left, right) => left.localeCompare(right))
      : [];

  return {
    connector: {
      destination: input.connectorConfig.parameterDestination,
      bindingKind: input.connectorConfig.parameterBindingKind,
    },
    bolt: {
      destination: input.boltConfig.parameterDestination ?? 'project',
      bindingKind: input.boltConfig.parameterBindingKind ?? 'type',
      projectCategories: sortedCategories(input.boltConfig.projectParameterCategories),
      modelingChoice: getFormFieldValue(input.boltConfig.boltModelingChoice),
      parameters: sanitizeRevitParameterNames(collectBoltConfiguredParameters(input.boltConfig)),
    },
    additional: {
      destination: input.additionalCategoriesConfig.parameterDestination ?? 'project',
      bindingKind: input.additionalCategoriesConfig.parameterBindingKind ?? 'instance',
      projectCategories: sortedCategories(input.additionalCategoriesConfig.projectParameterCategories),
      targets: collectAdditionalCategoryTargets(input.additionalCategoriesConfig).map((target) => ({
        label: target.label,
        targetObjectTypes: [...target.targetObjectTypes].sort((left, right) => left.localeCompare(right)),
        parameterNames: sanitizeRevitParameterNames(target.parameterNames),
      })),
    },
  };
}
